import type { ElementSettingsFormField } from '../../elements/types';
import { isRecord } from '../../../../../utils/typeGuards';
import {
    deleteValueAtPath,
    getRowsFieldItemFields,
    getValueAtPath,
    setValueAtPath,
} from './shared';

type NormalizePhase = 'default' | 'save';

function normalizeNumberDefault(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed.length) {
        return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
        return null;
    }

    return parsed;
}

function normalizeStringListDefault(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((entry): entry is string => typeof entry === 'string');
}

function normalizeDefinitionsDefault(value: unknown): unknown[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((entry: unknown) => entry);
}

function normalizeStringDefault(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return '';
}

function assertGroupField(field: ElementSettingsFormField): void {
    if (field.type !== 'group') {
        throw new Error(`Expected "${field.name}" to be a group field.`);
    }
}

function getGroupFieldItemFields(
    field: ElementSettingsFormField
): readonly ElementSettingsFormField[] {
    assertGroupField(field);

    const groupFields = field.fields;
    if (!Array.isArray(groupFields) || groupFields.length === 0) {
        throw new Error(`Expected group field "${field.name}" to define nested fields.`);
    }

    const validatedFields = groupFields.filter(
        (groupField): groupField is ElementSettingsFormField =>
            isRecord(groupField) && typeof groupField.name === 'string'
    );
    if (validatedFields.length !== groupFields.length) {
        throw new Error(`Expected group field "${field.name}" to define valid nested fields.`);
    }

    return validatedFields;
}

function normalizeSwarmTypesValue(value: unknown): unknown {
    // `null` means "supports every SWARM type" and must survive both the form
    // default and the save path, so only lists are copied. The explicit
    // `unknown` mapping keeps the copy free of `any` element types.
    return Array.isArray(value) ? value.map((entry: unknown) => entry) : value;
}

function normalizeScalarDefaultValue(field: ElementSettingsFormField, value: unknown): unknown {
    if (field.type === 'float') {
        return normalizeNumberDefault(value);
    }

    if (field.type === 'boolean') {
        return value === true;
    }

    if (field.type === 'stringList') {
        return normalizeStringListDefault(value);
    }

    if (field.type === 'swarmTypes') {
        return normalizeSwarmTypesValue(value);
    }

    if (field.type === 'definitions') {
        return normalizeDefinitionsDefault(value);
    }

    return normalizeStringDefault(value);
}

function normalizeScalarSaveValue(field: ElementSettingsFormField, value: unknown): unknown {
    if (field.type === 'rows') {
        return undefined;
    }

    if (field.type === 'boolean') {
        return value === true;
    }

    if (field.type === 'stringList') {
        if (!Array.isArray(value)) {
            return undefined;
        }

        const list = value.filter((entry): entry is string => typeof entry === 'string');
        if (field.required === true || list.length > 0) {
            return list;
        }

        return undefined;
    }

    if (field.type === 'swarmTypes') {
        return normalizeSwarmTypesValue(value);
    }

    if (field.type === 'definitions') {
        return Array.isArray(value) ? value : undefined;
    }

    if (value === null || typeof value === 'undefined') {
        return undefined;
    }

    if (field.type === 'float') {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : value;
        }

        return undefined;
    }

    if (typeof value === 'string') {
        if (value.length === 0) {
            return undefined;
        }

        return value;
    }

    return value;
}

type NormalizeFieldValueFn = (
    field: ElementSettingsFormField,
    value: unknown,
    phase: NormalizePhase
) => unknown;

function normalizeFieldTree(
    fields: readonly ElementSettingsFormField[],
    sourceValue: unknown,
    phase: NormalizePhase,
    normalizeField: NormalizeFieldValueFn
): Record<string, unknown> {
    const sourceRecord = isRecord(sourceValue) ? sourceValue : {};
    const normalizedRecord: Record<string, unknown> = {};

    fields.forEach((field) => {
        const currentValue = getValueAtPath(sourceRecord, field.name);
        const inputValue =
            phase === 'default' && typeof currentValue === 'undefined'
                ? field.default
                : currentValue;
        const normalizedValue = normalizeField(field, inputValue, phase);

        if (phase === 'save' && typeof normalizedValue === 'undefined') {
            deleteValueAtPath(normalizedRecord, field.name);
            return;
        }

        setValueAtPath(normalizedRecord, field.name, normalizedValue);
    });

    return normalizedRecord;
}

function normalizeSavedGroupValue(
    field: ElementSettingsFormField,
    normalizedGroup: Record<string, unknown>
): Record<string, unknown> | null | undefined {
    if (field.required === true) {
        return normalizedGroup;
    }

    if (
        (field.saveAsNullWhenMissingFields ?? []).some(
            (fieldName) => typeof getValueAtPath(normalizedGroup, fieldName) === 'undefined'
        )
    ) {
        return null;
    }

    if (Object.keys(normalizedGroup).length === 0) {
        return undefined;
    }

    return normalizedGroup;
}

function normalizeFieldValue(
    field: ElementSettingsFormField,
    value: unknown,
    phase: NormalizePhase
): unknown {
    if (field.type === 'rows') {
        const itemFields = getRowsFieldItemFields(field);

        if (phase === 'default') {
            const rows = Array.isArray(value) ? value : [];
            return rows.map((row) =>
                normalizeFieldTree(itemFields, row, 'default', normalizeFieldValue)
            );
        }

        if (!Array.isArray(value)) {
            return value;
        }

        return value.map((nextRow) =>
            normalizeFieldTree(itemFields, nextRow, 'save', normalizeFieldValue)
        );
    }

    if (field.type === 'group') {
        const groupFields = getGroupFieldItemFields(field);

        if (phase === 'default') {
            return normalizeFieldTree(groupFields, value, 'default', normalizeFieldValue);
        }

        if (!isRecord(value)) {
            return undefined;
        }

        const normalizedGroup = normalizeFieldTree(groupFields, value, 'save', normalizeFieldValue);
        return normalizeSavedGroupValue(field, normalizedGroup);
    }

    if (phase === 'default') {
        return normalizeScalarDefaultValue(field, value);
    }

    return normalizeScalarSaveValue(field, value);
}

export function normalizeFieldDefault(field: ElementSettingsFormField, value: unknown): unknown {
    return normalizeFieldValue(field, value, 'default');
}

export function normalizeFieldForSave(
    field: ElementSettingsFormField,
    validatedValue: unknown
): unknown {
    return normalizeFieldValue(field, validatedValue, 'save');
}

export function createNormalizedDefaultRecord(
    fields: readonly ElementSettingsFormField[],
    value: unknown
): Record<string, unknown> {
    return normalizeFieldTree(fields, value, 'default', normalizeFieldValue);
}
